import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, User, Menu, X, Globe, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { publicAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { trackSearch } from '../../lib/tracking';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount, openCart } = useCart();
  const { user, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState({ products: [], looks: [] });
  const [searching, setSearching] = React.useState(false);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/looks', label: t('nav.looks') },
    { href: '/quiz', label: t('nav.quiz') },
  ];

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Search functionality
  const handleSearch = React.useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults({ products: [], looks: [] });
      return;
    }

    setSearching(true);
    try {
      const response = await publicAPI.search(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleResultClick = (type, slug) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(type === 'product' ? `/products/${slug}` : `/looks/${slug}`);
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 glass bg-background/80 border-b border-border/50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-display text-2xl lg:text-3xl font-bold tracking-tight"
            data-testid="logo-link"
          >
            VYASTORE
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-foreground/80",
                  location.pathname === link.href 
                    ? "text-foreground" 
                    : "text-muted-foreground"
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Search Button */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSearchOpen(true)}
              data-testid="search-button"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="language-switcher">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('fr')}>
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('en')}>
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Account */}
            <Link to={user ? '/account' : '/login'} data-testid="account-link">
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            </Link>

            {/* Admin Link */}
            {isAdmin() && (
              <Link to="/admin" data-testid="admin-link">
                <Button variant="outline" size="sm" className="hidden sm:flex">
                  {t('nav.admin')}
                </Button>
              </Link>
            )}

            {/* Cart */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={openCart}
              data-testid="cart-button"
            >
              <ShoppingBag className="h-5 w-5" />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-foreground text-background text-xs flex items-center justify-center font-medium"
                >
                  {itemCount}
                </motion.span>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-border/50"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block py-2 text-base font-medium transition-colors",
                      location.pathname === link.href 
                        ? "text-foreground" 
                        : "text-muted-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                {isAdmin() && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-base font-medium text-muted-foreground"
                  >
                    {t('nav.admin')}
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
      {/* Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Rechercher</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Rechercher des produits ou looks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="text-base"
            />

            {searching && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            )}

            {!searching && searchQuery && (
              <div className="space-y-6">
                {/* Products Results */}
                {searchResults.products?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Produits ({searchResults.products.length})</h3>
                    <div className="space-y-2">
                      {searchResults.products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleResultClick('product', product.slug)}
                          className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          {product.images?.[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatPrice(product.variants?.[0]?.price_cents || 0)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Looks Results */}
                {searchResults.looks?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Looks ({searchResults.looks.length})</h3>
                    <div className="space-y-2">
                      {searchResults.looks.map((look) => (
                        <button
                          key={look.id}
                          onClick={() => handleResultClick('look', look.slug)}
                          className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          {look.image_url && (
                            <img
                              src={look.image_url}
                              alt={look.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-medium">{look.title}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {look.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Results */}
                {searchResults.products?.length === 0 && searchResults.looks?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucun résultat trouvé pour "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
    
